import React from "react";
import {
  FormControl,
  InputLabel,
  alpha,
  InputBase,
  styled,
  InputAdornment,
} from "@mui/material";

interface FormFieldProps {
  label: string;
  name: string;
  value: string;
  required?: boolean;
  error?: boolean;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  endIcon?: React.ReactNode;
}

const INPUT_HEIGHT = 48;

const BootstrapInput = styled(InputBase, {
  shouldForwardProp: (prop) => prop !== "error",
})<{ error?: boolean; value?: string }>(({ theme, error, value }) => {
  let inputBorderColor: string;
  if (error) {
    inputBorderColor = theme.palette.error.main;
  } else if (value && value.trim().length > 0) {
    inputBorderColor = theme.palette.primary.main;
  } else {
    inputBorderColor =
      theme.palette.mode === "dark"
        ? "rgba(255, 255, 255, 0.23)"
        : "rgba(0, 0, 0, 0.23)";
  }

  return {
    "label + &": {
      marginTop: theme.spacing(3),
    },
    "& .MuiInputBase-input": {
      height: INPUT_HEIGHT,
      borderRadius: 8,
      position: "relative",
      backgroundColor:
        theme.palette.mode === "dark"
          ? "rgba(255, 255, 255, 0.05)"
          : theme.palette.background.paper,
      border: "2px solid",
      borderColor: inputBorderColor,
      fontSize: 15,
      fontWeight: 400,
      width: "100%",
      padding: "0 16px",
      transition: theme.transitions.create(
        ["border-color", "background-color", "box-shadow"],
        {
          duration: 200,
        },
      ),
      "&:hover": {
        borderColor: error
          ? theme.palette.error.main
          : theme.palette.primary.main,
        backgroundColor:
          theme.palette.mode === "dark"
            ? "rgba(255, 255, 255, 0.08)"
            : theme.palette.action.hover,
      },
      "&:focus": {
        boxShadow: `${alpha(
          error ? theme.palette.error.main : theme.palette.primary.main,
          0.2,
        )} 0 0 0 3px`,
        borderColor: error
          ? theme.palette.error.main
          : theme.palette.primary.main,
        backgroundColor: theme.palette.background.paper,
      },
      ...(theme.palette.mode === "dark" && {
        backgroundColor: "rgba(255, 255, 255, 0.05)",
      }),
    },
  };
});

const FormField: React.FC<FormFieldProps> = ({
  label,
  name,
  value,
  required = false,
  error = false,
  onChange,
  endIcon = null,
}) => (
  <FormControl fullWidth variant="standard">
    <InputLabel
      shrink
      htmlFor={`${label}-input`}
      sx={{
        fontWeight: 500,
        fontSize: "0.95rem",
        color: error ? "error.main" : "text.primary",
        "&.Mui-focused": {
          color: error ? "error.main" : "primary.main",
        },
      }}
    >
      {label}
    </InputLabel>
    <BootstrapInput
      id={`${label}-input`}
      name={name}
      value={value}
      onChange={onChange}
      required={required}
      error={error}
      endAdornment={
        endIcon && (
          <InputAdornment
            position="end"
            sx={{
              position: "absolute",
              right: 12,
              color: (theme) =>
                value && value.trim().length > 0
                  ? theme.palette.primary.main
                  : alpha(theme.palette.text.secondary, 0.5),
              transition: "color 0.2s ease",
            }}
          >
            {endIcon}
          </InputAdornment>
        )
      }
    />
  </FormControl>
);

export default FormField;
